import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ALACARTE_ITEMS,
  fmtCents,
  startAlacarteCheckout,
  AlacarteCheckoutError,
  type AlacarteItem,
  type PurchaseType,
} from "@/lib/billing/alacarte";
import { useFeatureActive } from "@/lib/entitlements/useEntitlements";
import { useFounder } from "@/lib/founder/useFounder";
import { toast } from "sonner";
import type { AppId } from "@/lib/apps";

interface Props {
  item: AlacarteItem;
  /** Active app context. Required to purchase resonate_pack. */
  app?: AppId;
  /** Required for event-locked items. */
  eventId?: string;
  onAuthRequired: () => void;
  onOnboardingRequired: () => void;
  signedIn: boolean;
  onboarded: boolean;
}

const AlacarteCard = ({
  item,
  app,
  eventId,
  onAuthRequired,
  onOnboardingRequired,
  signedIn,
  onboarded,
}: Props) => {
  const { active } = useFeatureActive(item.key);
  const { isFounder } = useFounder();
  const [busy, setBusy] = useState<PurchaseType | null>(null);

  const resonateBlocked =
    item.appScope === "mindlynq" && app !== undefined && app !== "mindlynq";

  const handleBuy = async (purchaseType: PurchaseType) => {
    if (!signedIn) {
      onAuthRequired();
      return;
    }
    if (!onboarded) {
      onOnboardingRequired();
      return;
    }
    if (item.requiresEvent && !eventId) {
      toast.error("Pick an event first to use this add-on.");
      return;
    }
    if (item.appScope === "mindlynq" && app !== "mindlynq") {
      toast.error("RESONATE is only available inside MindLynq.");
      return;
    }

    setBusy(purchaseType);
    try {
      const { url } = await startAlacarteCheckout({
        featureKey: item.key,
        purchaseType,
        eventId,
        app: item.appScope ?? app,
      });
      window.location.href = url;
    } catch (err) {
      const msg =
        err instanceof AlacarteCheckoutError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not start checkout.";
      toast.error(msg);
      setBusy(null);
    }
  };

  return (
    <article className="rounded-2xl border border-border bg-card p-6 flex flex-col">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{item.name}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{item.tagline}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {isFounder ? (
            <Badge variant="default" className="text-[10px]">
              Founder · Free
            </Badge>
          ) : (
            active && (
              <Badge variant="default" className="text-[10px]">
                Active
              </Badge>
            )
          )}
          {item.appScope === "mindlynq" && (
            <Badge variant="outline" className="text-[10px]">
              MindLynq
            </Badge>
          )}
          {item.requiresEvent && (
            <Badge variant="outline" className="text-[10px]">
              Event-locked
            </Badge>
          )}
        </div>
      </header>

      <p className="mt-4 text-sm text-muted-foreground flex-1">
        {item.description}
      </p>

      <div className="mt-5 flex flex-col gap-2">
        {isFounder ? (
          <Button disabled variant="outline" className="w-full">
            Included with Founder access
          </Button>
        ) : (
          item.options.map((opt) => {
            const label =
              opt.type === "monthly"
                ? `${fmtCents(opt.amountCents)} ${opt.unit}`
                : `${fmtCents(opt.amountCents)} · ${opt.unit}`;
            const cta =
              busy === opt.type
                ? "Starting…"
                : opt.type === "monthly"
                  ? "Subscribe"
                  : "Buy";
            return (
              <Button
                key={opt.type}
                onClick={() => handleBuy(opt.type)}
                disabled={busy !== null || resonateBlocked}
                variant={opt.type === "monthly" ? "default" : "outline"}
                className="w-full justify-between"
              >
                <span>{cta}</span>
                <span className="text-xs opacity-90">{label}</span>
              </Button>
            );
          })
        )}
      </div>

      {!isFounder && resonateBlocked && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          Switch to MindLynq to purchase RESONATE.
        </p>
      )}
      {!isFounder && item.requiresEvent && !eventId && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          Pick an event in-app to enable purchase.
        </p>
      )}
    </article>
  );
};

export default AlacarteCard;
export { ALACARTE_ITEMS };
